'use client';

import { useState, useEffect, useLayoutEffect } from 'react';

interface UseAnimateInOptions {
  /**
   * Delay in milliseconds before the animation starts
   * @default 0
   */
  delay?: number;
  /**
   * Duration of the animation in milliseconds
   * @default 500
   */
  duration?: number;
  /**
   * Distance to translate from in pixels (positive = translate down, negative = translate up)
   * @default 20
   */
  translateDistance?: number;
  /**
   * Whether to start the animation immediately on mount
   * @default true
   */
  autoStart?: boolean;
  /**
   * Easing function for the transition
   * @default 'ease-out'
   */
  easing?: string;
}

interface UseAnimateInReturn {
  /**
   * Whether the element is currently visible (animated in)
   */
  isVisible: boolean;
  /**
   * Style object to apply to the element
   */
  style: React.CSSProperties;
  /**
   * Manually trigger the animation
   */
  trigger: () => void;
  /**
   * Reset the animation state
   */
  reset: () => void;
}

/**
 * Hook for animating elements in with fade and translate Y effect
 * 
 * @example
 * ```tsx
 * const { isVisible, style } = useAnimateIn({ delay: 300 });
 * 
 * return (
 *   <div style={style}>
 *     Content that fades in and slides up
 *   </div>
 * );
 * ```
 * 
 * @example
 * ```tsx
 * const { isVisible, style, trigger } = useAnimateIn({ autoStart: false });
 * 
 * return (
 *   <>
 *     <button onClick={trigger}>Animate</button>
 *     <div style={style}>Content</div>
 *   </>
 * );
 * ```
 */
export function useAnimateIn(options: UseAnimateInOptions = {}): UseAnimateInReturn {
  const {
    delay = 0,
    duration = 500,
    translateDistance = 20,
    autoStart = true,
    easing = 'ease-out'
  } = options;

  const [isVisible, setIsVisible] = useState(false);
  const [prefersReducedMotion, setPrefersReducedMotion] = useState(false);

  // Check for reduced motion preference
  useEffect(() => {
    const checkReducedMotion = () => {
      const mediaQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
      setPrefersReducedMotion(mediaQuery.matches);
      // If reduced motion, immediately show the element
      if (mediaQuery.matches && autoStart) {
        setIsVisible(true);
      }
    };
    
    checkReducedMotion(); // Initial check
    
    const mediaQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
    const handleChange = () => {
      setPrefersReducedMotion(mediaQuery.matches);
      if (mediaQuery.matches) {
        setIsVisible(true);
      }
    };
    
    // Listen for changes (some browsers support this)
    if (mediaQuery.addEventListener) {
      mediaQuery.addEventListener('change', handleChange);
      return () => mediaQuery.removeEventListener('change', handleChange);
    } else {
      // Fallback for older browsers
      mediaQuery.addListener(handleChange);
      return () => mediaQuery.removeListener(handleChange);
    }
  }, [autoStart]);

  useEffect(() => {
    if (!autoStart || prefersReducedMotion) return;

    const timer = setTimeout(() => {
      setIsVisible(true);
    }, delay);

    return () => clearTimeout(timer);
  }, [delay, autoStart, prefersReducedMotion]);

  const trigger = () => {
    if (prefersReducedMotion) {
      setIsVisible(true);
      return;
    }
    setTimeout(() => {
      setIsVisible(true);
    }, delay);
  };

  const reset = () => {
    if (!prefersReducedMotion) {
      setIsVisible(false);
    }
  };

  const style: React.CSSProperties = {
    transition: prefersReducedMotion ? 'none' : `all ${duration}ms ${easing}`,
    transform: isVisible ? 'translateY(0)' : `translateY(${translateDistance}px)`,
    opacity: isVisible ? 1 : 0
  };

  return {
    isVisible,
    style,
    trigger,
    reset
  };
}

/**
 * Utility function to create animation styles for staggered animations
 * 
 * @example
 * ```tsx
 * {items.map((item, index) => (
 *   <div key={item.id} style={getStaggeredAnimateInStyle(index, { delay: 100 })}>
 *     {item.content}
 *   </div>
 * ))}
 * ```
 */
export function getStaggeredAnimateInStyle(
  index: number,
  options: UseAnimateInOptions & { staggerDelay?: number } = {}
): React.CSSProperties {
  const {
    delay = 0,
    duration = 500,
    translateDistance = 20,
    easing = 'ease-out',
    staggerDelay = 80
  } = options;

  const totalDelay = delay + (index * staggerDelay);

  return {
    transitionProperty: 'transform, opacity',
    transitionDuration: `${duration}ms`,
    transitionTimingFunction: easing,
    transitionDelay: `${totalDelay}ms`,
    transform: 'translateY(0)',
    opacity: 1
  };
}

/**
 * Hook for managing staggered animations of multiple items
 * 
 * @example
 * ```tsx
 * const { getItemStyle } = useStaggeredAnimateIn({ delay: 300, staggerDelay: 100 });
 * 
 * return (
 *   <>
 *     {items.map((item, index) => (
 *       <div key={item.id} style={getItemStyle(index)}>
 *         {item.content}
 *       </div>
 *     ))}
 *   </>
 * );
 * ```
 */
export function useStaggeredAnimateIn(options: UseAnimateInOptions & { staggerDelay?: number } = {}) {
  const {
    delay = 0,
    duration = 500,
    translateDistance = 20,
    autoStart = true,
    easing = 'ease-out',
    staggerDelay = 80
  } = options;

  const [itemsVisible, setItemsVisible] = useState(false);
  const [prefersReducedMotion, setPrefersReducedMotion] = useState(false);

  // Check for reduced motion preference
  useEffect(() => {
    const checkReducedMotion = () => {
      const mediaQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
      setPrefersReducedMotion(mediaQuery.matches);
      // If reduced motion, immediately show items
      if (mediaQuery.matches && autoStart) {
        setItemsVisible(true);
      }
    };
    
    checkReducedMotion(); // Initial check
    
    const mediaQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
    const handleChange = () => {
      setPrefersReducedMotion(mediaQuery.matches);
      if (mediaQuery.matches) {
        setItemsVisible(true);
      }
    };
    
    // Listen for changes (some browsers support this)
    if (mediaQuery.addEventListener) {
      mediaQuery.addEventListener('change', handleChange);
      return () => mediaQuery.removeEventListener('change', handleChange);
    } else {
      // Fallback for older browsers
      mediaQuery.addListener(handleChange);
      return () => mediaQuery.removeListener(handleChange);
    }
  }, [autoStart]);

  // Use useLayoutEffect to set initial state before paint
  useLayoutEffect(() => {
    if (!autoStart) {
      setItemsVisible(false);
    } else if (!prefersReducedMotion) {
      // Ensure items start hidden only if not reduced motion
      setItemsVisible(false);
    }
  }, [autoStart, prefersReducedMotion]);

  useEffect(() => {
    if (!autoStart || prefersReducedMotion) {
      return;
    }

    // Small delay to ensure DOM is ready, then start animation
    const timer = setTimeout(() => {
      setItemsVisible(true);
    }, delay);

    return () => {
      clearTimeout(timer);
    };
  }, [delay, autoStart, prefersReducedMotion]);

  const trigger = () => {
    if (prefersReducedMotion) {
      setItemsVisible(true);
      return;
    }
    setTimeout(() => {
      setItemsVisible(true);
    }, delay);
  };

  const reset = () => {
    if (!prefersReducedMotion) {
      setItemsVisible(false);
    }
  };

  const getItemStyle = (index: number): React.CSSProperties => {
    const itemDelay = prefersReducedMotion ? 0 : index * staggerDelay;
    
    return {
      transitionProperty: (itemsVisible && !prefersReducedMotion) ? 'transform, opacity' : 'none',
      transitionDuration: (itemsVisible && !prefersReducedMotion) ? `${duration}ms` : '0ms',
      transitionTimingFunction: (itemsVisible && !prefersReducedMotion) ? easing : 'ease',
      transitionDelay: (itemsVisible && !prefersReducedMotion) ? `${itemDelay}ms` : '0ms',
      transform: itemsVisible ? 'translateY(0)' : `translateY(${translateDistance}px)`,
      opacity: itemsVisible ? 1 : 0,
      // Don't override color transitions - let CSS classes handle them
      // Only set transform/opacity transitions for the stagger animation
    };
  };

  return {
    itemsVisible,
    getItemStyle,
    trigger,
    reset
  };
}

interface UseScaleInOptions {
  /**
   * Delay in milliseconds before the animation starts
   * @default 0
   */
  delay?: number;
  /**
   * Duration of the animation in milliseconds
   * @default 500
   */
  duration?: number;
  /**
   * Initial scale value
   * @default 0
   */
  initialScale?: number;
  /**
   * Whether to start the animation immediately on mount
   * @default true
   */
  autoStart?: boolean;
}

interface UseScaleInReturn {
  /**
   * Whether the element is currently visible (animated in)
   */
  isVisible: boolean;
  /**
   * Style object to apply to the element
   */
  style: React.CSSProperties;
  /**
   * Manually trigger the animation
   */
  trigger: () => void;
  /**
   * Reset the animation state
   */
  reset: () => void;
}

/**
 * Hook for animating elements in with scale effect (ease-in)
 * 
 * @example
 * ```tsx
 * const { style } = useScaleIn({ delay: 300 });
 * 
 * return (
 *   <div style={style}>
 *     Content that scales in
 *   </div>
 * );
 * ```
 */
export function useScaleIn(options: UseScaleInOptions = {}): UseScaleInReturn {
  const {
    delay = 0,
    duration = 500,
    initialScale = 0,
    autoStart = true
  } = options;

  const [isVisible, setIsVisible] = useState(false);
  const [prefersReducedMotion, setPrefersReducedMotion] = useState(false);

  // Check for reduced motion preference
  useEffect(() => {
    const checkReducedMotion = () => {
      const mediaQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
      setPrefersReducedMotion(mediaQuery.matches);
      // If reduced motion, immediately show the element
      if (mediaQuery.matches && autoStart) {
        setIsVisible(true);
      }
    };
    
    checkReducedMotion(); // Initial check
    
    const mediaQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
    const handleChange = () => {
      setPrefersReducedMotion(mediaQuery.matches);
      if (mediaQuery.matches) {
        setIsVisible(true);
      }
    };
    
    // Listen for changes (some browsers support this)
    if (mediaQuery.addEventListener) {
      mediaQuery.addEventListener('change', handleChange);
      return () => mediaQuery.removeEventListener('change', handleChange);
    } else {
      // Fallback for older browsers
      mediaQuery.addListener(handleChange);
      return () => mediaQuery.removeListener(handleChange);
    }
  }, [autoStart]);

  useEffect(() => {
    if (!autoStart || prefersReducedMotion) return;

    const timer = setTimeout(() => {
      setIsVisible(true);
    }, delay);

    return () => clearTimeout(timer);
  }, [delay, autoStart, prefersReducedMotion]);

  const trigger = () => {
    if (prefersReducedMotion) {
      setIsVisible(true);
      return;
    }
    setTimeout(() => {
      setIsVisible(true);
    }, delay);
  };

  const reset = () => {
    if (!prefersReducedMotion) {
      setIsVisible(false);
    }
  };

  const style: React.CSSProperties = {
    transition: prefersReducedMotion ? 'none' : `transform ${duration}ms cubic-bezier(0.34, 1.56, 0.64, 1)`,
    transform: isVisible ? 'scale(1)' : `scale(${initialScale})`,
    transformOrigin: 'center'
  };

  return {
    isVisible,
    style,
    trigger,
    reset
  };
}

